import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card } from "@/design-system";
import { Icon } from "@/components/Icon";
import { PasswordInput } from "@/components/PasswordInput";
import { ApiRequestError, api } from "@/api/client";
import { useAuth } from "@/auth";
import type { AuthResponse } from "@/lib/types";

/**
 * Change-password card for the account settings screen.
 *
 * - Before hitting the API: all three fields are required and the
 *   confirmation must match the new password (client-side checks so an
 *   obvious mistake never leaves the page).
 * - Server `422` field errors are rendered inline per field, exactly
 *   like `AuthScreen` does for login/register.
 * - `401` (session died server-side) drops the user back on `/login`.
 * - On `200` the fresh token from the response is stored via
 *   `useAuth().setSession` — every session was revoked server-side, so
 *   the new token is what keeps the user signed in.
 */

interface FieldErrors {
  current_password?: string;
  password?: string;
  password_confirmation?: string;
}

interface PasswordChangeParams {
  current_password: string;
  password: string;
  password_confirmation: string;
}

export function ChangePasswordForm() {
  const { token, setSession } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation<AuthResponse, Error, PasswordChangeParams>({
    mutationFn: (params) =>
      api.patch<AuthResponse>("/api/me/password", params, { token }),
  });

  const isPending = mutation.isPending;

  function classifyError(err: unknown): {
    fieldErrors: FieldErrors;
    general: string | null;
    sessionExpired: boolean;
  } {
    if (!(err instanceof ApiRequestError)) {
      return { fieldErrors: {}, general: t("settings.errorGeneric"), sessionExpired: false };
    }

    if (err.status === 401) {
      return {
        fieldErrors: {},
        general: t("settings.errorSessionExpired"),
        sessionExpired: true,
      };
    }

    if (err.status === 422) {
      const errors = err.body?.errors ?? {};
      const next: FieldErrors = {};

      for (const key of ["current_password", "password", "password_confirmation"] as const) {
        const first = pickFirstMessage(errors[key]);
        if (first) next[key] = first;
      }

      return { fieldErrors: next, general: null, sessionExpired: false };
    }

    return { fieldErrors: {}, general: err.message, sessionExpired: false };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (isPending) return;

    const nextErrors: FieldErrors = {};
    if (!currentPassword.trim()) nextErrors.current_password = t("settings.errorRequired");
    if (!password.trim()) nextErrors.password = t("settings.errorRequired");
    if (!passwordConfirmation.trim()) {
      nextErrors.password_confirmation = t("settings.errorRequired");
    } else if (password !== passwordConfirmation) {
      nextErrors.password_confirmation = t("settings.errorMismatch");
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setGeneralError(null);
      setSuccess(false);
      return;
    }

    setFieldErrors({});
    setGeneralError(null);
    setSuccess(false);

    try {
      const res = await mutation.mutateAsync({
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      });

      setSession(res.token, res.user);
      setSuccess(true);
      setCurrentPassword("");
      setPassword("");
      setPasswordConfirmation("");
    } catch (err) {
      const { fieldErrors: nextFieldErrors, general, sessionExpired } = classifyError(err);
      setFieldErrors(nextFieldErrors);
      setGeneralError(general);
      if (sessionExpired) {
        navigate("/login", { replace: true });
      }
    }
  }

  function clearErrorsOnEdit() {
    if (Object.keys(fieldErrors).length > 0 || generalError) {
      setFieldErrors({});
      setGeneralError(null);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      <PasswordInput
        label={t("settings.currentPasswordLabel")}
        name="current_password"
        autoComplete="current-password"
        placeholder={t("settings.currentPasswordPlaceholder")}
        value={currentPassword}
        onChange={(e) => {
          setCurrentPassword(e.target.value);
          clearErrorsOnEdit();
        }}
        required
        disabled={isPending}
        invalid={Boolean(fieldErrors.current_password)}
        hint={fieldErrors.current_password}
        iconLeft={<Icon name="user" size={18} />}
      />
      <PasswordInput
        label={t("settings.newPasswordLabel")}
        name="password"
        autoComplete="new-password"
        placeholder={t("settings.newPasswordPlaceholder")}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          clearErrorsOnEdit();
        }}
        required
        minLength={12}
        disabled={isPending}
        invalid={Boolean(fieldErrors.password)}
        hint={fieldErrors.password ?? t("settings.newPasswordHint")}
        iconLeft={<Icon name="settings" size={18} />}
      />
      <PasswordInput
        label={t("settings.confirmPasswordLabel")}
        name="password_confirmation"
        autoComplete="new-password"
        placeholder={t("settings.confirmPasswordPlaceholder")}
        value={passwordConfirmation}
        onChange={(e) => {
          setPasswordConfirmation(e.target.value);
          clearErrorsOnEdit();
        }}
        required
        disabled={isPending}
        invalid={Boolean(fieldErrors.password_confirmation)}
        hint={fieldErrors.password_confirmation}
        iconLeft={<Icon name="settings" size={18} />}
      />

      {generalError && (
        <Card
          role="alert"
          padding="10px 14px"
          style={{
            background: "var(--status-danger-bg)",
            border: "1px solid var(--rust-500)",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--status-danger-fg)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            <span style={{ display: "inline-flex", color: "var(--rust-500)" }}>
              <Icon name="flame" size={18} />
            </span>
            {generalError}
          </div>
        </Card>
      )}

      {success && (
        <Card
          role="status"
          padding="10px 14px"
          style={{
            background: "var(--status-success-bg)",
            border: "1px solid var(--sage-600)",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--status-success-fg)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            <span style={{ display: "inline-flex", color: "var(--sage-600)" }}>
              <Icon name="check" size={18} />
            </span>
            {t("settings.success")}
          </div>
        </Card>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={isPending}
        data-testid="change-password-submit"
      >
        {isPending ? t("settings.updatingPassword") : t("settings.updatePassword")}
      </Button>
    </form>
  );
}

/** Pull a human-readable message out of a string[] or a string. */
function pickFirstMessage(raw: unknown): string | undefined {
  if (Array.isArray(raw)) {
    const first = raw[0];
    return typeof first === "string" ? first : undefined;
  }
  if (typeof raw === "string") return raw;
  return undefined;
}